import styled from 'styled-components'
import pluralize from 'pluralize'

import { formatPercentage } from 'components/utils/numbers'
import SvgIconHappy from 'components/icons/Happy'
import SvgIconIndifferent from 'components/icons/Indifferent'
import SvgIconUnhappy from 'components/icons/Unhappy'

type SizeProp = 'compact' | 'small' | 'medium' | 'large'

interface ReviewScoreProps {
  score: number | string
  size?: SizeProp
  handleShowReviews?: () => any
  count?: number
}

const ReviewScoreContainer = styled.div<{ size?: SizeProp }>`
  display: flex;
  align-items: center;
  color: ${({ size, theme }) =>
    size === 'compact'
      ? theme?.['-ds-success-300']?.value
      : theme?.['-ds-core-400']?.value};
  font-weight: ${({ size }) => (size === 'compact' ? '500' : '300')};
  font-size: ${({ size }) => {
    switch (size) {
      case 'compact':
        return '10px'
      case 'small':
        return '14px'
      case 'medium':
        return '16px'
      case 'large':
        return '18px'
      default:
        return '16px'
    }
  }};
  letter-spacing: ${({ size }) => (size === 'compact' ? '0.08em' : '0.01em')};
  background-color: ${({ size, theme }) =>
    size === 'compact' ? theme?.['-ds-success-100']?.value : 'transparent'};
  padding: ${({ size }) => (size === 'compact' ? '5px 7px' : '0')};
  border-radius: ${({ size }) => (size === 'compact' ? '80px' : '0')};

  svg {
    color: inherit;
    margin-right: ${({ size }) => {
      switch (size) {
        case 'compact':
        case 'small':
          return '6px'
        case 'medium':
          return '10px'
        case 'large':
          return '16px'
        default:
          return '10px'
      }
    }};
    width: ${({ size }) => {
      switch (size) {
        case 'compact':
        case 'small':
          return '16px'
        case 'medium':
          return '24px'
        case 'large':
          return '30px'
        default:
          return '24px'
      }
    }};
  }
`

const ReviewScoreButton = styled.span`
  margin-left: 5px;

  button {
    background-color: transparent;
    border: none;
    text-decoration: underline;
    text-underline-offset: 1px;
    text-decoration-thickness: 1px;
    margin-left: 5px;
    padding: 0;
  }
`

const parseReviewScore = (score: number | string) => {
  if (!score || score === '') return null

  const scoreFloat = typeof score === 'string' ? parseFloat(score) : score

  if (Number.isNaN(scoreFloat)) return null

  return scoreFloat
}

const ScoreIcon = ({ score }: { score: number }) => {
  if (score > 0.65) return <SvgIconHappy />
  if (score > 0.32) return <SvgIconIndifferent />
  if (score > 0) return <SvgIconUnhappy />
  return <SvgIconHappy />
}

const ReviewScore = ({
  handleShowReviews,
  size,
  score,
  count,
}: ReviewScoreProps) => {
  const parsedScore = parseReviewScore(score)

  if (!parsedScore || parsedScore <= 0) {
    return null
  }

  return (
    <ReviewScoreContainer size={size}>
      <ScoreIcon score={parsedScore} />
      {`${formatPercentage(parsedScore)}${
        size !== 'compact' ? ' Recommended' : ''
      }`}

      {handleShowReviews && count > 0 && (
        <ReviewScoreButton>
          &#8226;
          <button type="button" onClick={handleShowReviews}>
            {`See ${count || 'all'} ${pluralize('reviews', count)}`}
          </button>
        </ReviewScoreButton>
      )}
    </ReviewScoreContainer>
  )
}

export default ReviewScore
